/**
 * @fileoverview City-scoped neighborhood footprint path lookup (WEB-648).
 *
 * Fetches `GET /api/v1/neighborhoods?city=…&include=boundary` once per city
 * (same idiom as `useNeighborhoodCentroids`) and exposes a slug → path
 * lookup with an explicit discriminated result so consumers can render
 * honest absence instead of a broken glyph.
 *
 * Successful boundary payloads are effectively immutable — they are cached in
 * a module-level Map keyed by city code so remounts / multiple consumers do
 * not re-hit the network (and Discover cold load stays a single
 * `/events/discover` fetch). Failed fetches are not cached, so a later mount
 * can retry after a transient outage.
 */

'use client';

import * as React from 'react';
import type { CityCode } from '@loci/city-editions';
import { neighborhoodsListResponseSchema } from '@loci/validation';

import { API_ENDPOINTS } from '@/config/api';
import type { CityFootprintProjection } from '@/lib/neighborhoods/footprints';
import {
  toCityFootprintProjection,
  toNeighborhoodFootprintPaths,
} from '@/lib/neighborhoods/footprints';
import {
  getStaticFootprintFallbackPath,
  STATIC_FOOTPRINT_FALLBACK_PATH,
} from '@/lib/neighborhoods/static-fallback';

/** Discriminated result of looking up one neighborhood slug. */
export type NeighborhoodFootprintResult =
  | { status: 'loading' }
  | { status: 'error'; path: string }
  | { status: 'unknown-slug' }
  | { status: 'loaded'; path: string };

type LoadedCacheEntry = {
  status: 'loaded';
  /** Per-neighborhood paths, each fitted to its own bbox (44px hoodmarks). */
  paths: ReadonlyMap<string, string>;
  /**
   * The same outlines in one city-wide frame (WEB-654 hero art), or `null`
   * when the payload carried no drawable geometry. Derived here so both
   * consumers share a single fetch and a single cache entry.
   */
  city: CityFootprintProjection | null;
};

type FetchResult = LoadedCacheEntry | { status: 'error' };

const footprintCache = new Map<string, LoadedCacheEntry>();
const inflight = new Map<string, Promise<FetchResult>>();

function cacheKey(cityCode: CityCode): string {
  return cityCode.toUpperCase();
}

async function fetchFootprints(cityCode: CityCode): Promise<FetchResult> {
  const key = cacheKey(cityCode);
  const cached = footprintCache.get(key);
  if (cached) return cached;

  const existing = inflight.get(key);
  if (existing) return existing;

  // Intentionally no AbortSignal: the request is shared across consumers via
  // the module cache. Unmounting one subscriber must not cancel the fetch.
  const promise = (async (): Promise<FetchResult> => {
    try {
      const url = `${API_ENDPOINTS.rest}/api/v1/neighborhoods?city=${encodeURIComponent(cityCode)}&include=boundary`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) return { status: 'error' };
      const raw: unknown = await res.json();
      const parsed = neighborhoodsListResponseSchema.safeParse(raw);
      if (!parsed.success) return { status: 'error' };
      const entry: LoadedCacheEntry = {
        status: 'loaded',
        paths: toNeighborhoodFootprintPaths(parsed.data.neighborhoods),
        city: toCityFootprintProjection(parsed.data.neighborhoods),
      };
      footprintCache.set(key, entry);
      return entry;
    } catch {
      return { status: 'error' };
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

type HookState =
  | { cityKey: string; status: 'loading' }
  | { cityKey: string; status: 'error' }
  | { cityKey: string; status: 'loaded'; entry: LoadedCacheEntry };

/**
 * Shared subscription to one city's boundary payload.
 *
 * Both public hooks read this, so a page that renders hoodmarks AND the hero
 * art issues a single `/neighborhoods?include=boundary` request.
 *
 * @returns `null` when no city is selected. Callers must ignore state whose
 *   `cityKey` differs from the requested city — on the pre-effect render after
 *   `cityCode` changes it still holds the previous city's geometry.
 */
function useCityFootprintState(cityCode: CityCode | undefined): HookState | null {
  const cityKey = cityCode ? cacheKey(cityCode) : null;

  const [state, setState] = React.useState<HookState | null>(() => {
    if (!cityKey) return null;
    const cached = footprintCache.get(cityKey);
    if (cached) return { cityKey, status: 'loaded', entry: cached };
    return { cityKey, status: 'loading' };
  });

  React.useEffect(() => {
    if (!cityCode || !cityKey) {
      setState(null);
      return;
    }

    const cached = footprintCache.get(cityKey);
    if (cached) {
      setState({ cityKey, status: 'loaded', entry: cached });
      return;
    }

    setState({ cityKey, status: 'loading' });
    let cancelled = false;

    void fetchFootprints(cityCode).then((result) => {
      if (cancelled) return;
      if (result.status === 'loaded') {
        setState({ cityKey, status: 'loaded', entry: result });
      } else {
        setState({ cityKey, status: 'error' });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [cityCode, cityKey]);

  if (!cityKey) return null;
  // Ignore state belonging to a previous city so we never return another
  // city's geometry.
  if (state && state.cityKey === cityKey) return state;
  // Same cache read as the `useState` initializer above: on the pre-effect
  // render after `cityCode` changes, a city already in the cache resolves
  // synchronously instead of flashing a loading frame that the effect would
  // immediately overwrite.
  const cached = footprintCache.get(cityKey);
  return cached ? { cityKey, status: 'loaded', entry: cached } : { cityKey, status: 'loading' };
}

/**
 * Look up a normalised SVG path for `slug` within `cityCode`.
 *
 * Returns a discriminated result covering loading / loaded / error /
 * unknown-slug. On fetch failure, `error` carries
 * {@link STATIC_FOOTPRINT_FALLBACK_PATH} so the row can still render.
 */
export function useNeighborhoodFootprints(
  cityCode: CityCode | undefined,
  slug: string | undefined,
): NeighborhoodFootprintResult {
  const state = useCityFootprintState(cityCode);

  if (!cityCode || !slug || !state) {
    return { status: 'unknown-slug' };
  }

  if (state.status === 'loading') {
    return { status: 'loading' };
  }

  if (state.status === 'error') {
    return { status: 'error', path: getStaticFootprintFallbackPath() };
  }

  const path = state.entry.paths.get(slug);
  if (!path) {
    return { status: 'unknown-slug' };
  }

  return { status: 'loaded', path };
}

/** Discriminated result of projecting a whole city's footprints (WEB-654). */
export type CityFootprintsResult =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'empty' }
  | { status: 'loaded'; projection: CityFootprintProjection };

/**
 * Project every drawable neighborhood outline for `cityCode` into one shared
 * unit viewBox — the geometry behind `DiscoverHeroStripArt` (WEB-654).
 * WEB-775 unmounted that component from the default `/discover` header; this
 * hook stays for any future real-geometry surface and for the hook's tests.
 *
 * Deliberately has NO fallback path: unlike a row hoodmark, which anchors a
 * label and degrades to {@link STATIC_FOOTPRINT_FALLBACK_PATH}, the hero layer
 * is pure decoration. A generic shape there would be a placeholder claiming to
 * be the city, so `error` / `empty` mean "render nothing".
 */
export function useCityNeighborhoodFootprints(
  cityCode: CityCode | undefined,
): CityFootprintsResult {
  const state = useCityFootprintState(cityCode);

  if (!cityCode || !state) return { status: 'empty' };
  if (state.status === 'loading') return { status: 'loading' };
  if (state.status === 'error') return { status: 'error' };

  const projection = state.entry.city;
  return projection ? { status: 'loaded', projection } : { status: 'empty' };
}

/** Test-only: clear the module-level cache between cases. */
export function __resetNeighborhoodFootprintCacheForTests(): void {
  footprintCache.clear();
  inflight.clear();
}

export { STATIC_FOOTPRINT_FALLBACK_PATH };
